import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';
import ButtonCustom from 'components/ButtonCustom';
import ButtonIcon from 'components/ButtonIcon';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import { TypographyLocale } from 'components/TypographyLocale';
import UseAuthContext from 'context/AuthContext';
import { useFormik } from 'formik';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getOrganizationTransactionsCSV } from 'services/admin-organizations';
import { adminTopUp, getOrganizationTransactions } from 'services/business-setting-service';
import { getProjectLists } from 'services/project-service';
import { getPricingPlans } from 'services/pricing-service';
import shortid from 'shortid';
import { USER_ROLES } from 'utils/constants';
import { IAdminTopUp, Order } from 'utils/contracts';
import { package_options } from 'utils/Formatter';
import { calcTotalAccountPoints, getErrorHtmlContent } from 'utils/utils';
import * as Yup from 'yup';
import Transaction from './Transaction';

const getActiveUnlimitedPackageId = (organization: any): string | null => {
  if (!organization?.transactions) return null;
  
  const now = new Date();
  const transactions = Object.values(organization.transactions || {});
  
  const activeUnlimited: any = transactions.find((t: any) => 
    t.status === 'success' && 
    t.unlimited === true &&
    t.type === 'CREDIT' &&
    new Date(t.expiry_date) > now
  );
  
  return activeUnlimited ? activeUnlimited.plan_id : null;
};

let DEFAULT_ORDER = 'desc',
  DEFAULT_ORDER_BY = 'createdAt';

// Filter to exclude unlimited packages (they are handled in Packages tab)
const creditPackageOptions = package_options.filter(
  (option: any) => !option.value.startsWith('PACKAGE_UNLIMITED_')
);

type CreditsProjectOption = { id: string; name: string };

const textFieldStyles = {
  '& .MuiInputBase-root': {
    height: '50px', // Custom height
    padding: '20px 0px', // Adjust padding as needed
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(12px, 14px) scale(1)', // Adjust label position
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
    transform: 'translate(12px, -10px) scale(0.75)',
  },
};

/** Same 50px row as package Autocomplete; size small + no double vertical padding from textFieldStyles. */
const creditsFilterFieldSx = {
  ...textFieldStyles,
  '& .MuiInputBase-root': {
    height: '50px',
    minHeight: '50px',
    boxSizing: 'border-box',
    padding: 0,
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-root': {
    height: '50px',
    minHeight: '50px',
    boxSizing: 'border-box',
    paddingTop: 0,
    paddingBottom: 0,
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-root:not(.MuiAutocomplete-inputRoot)': {
    paddingLeft: '14px',
    paddingRight: '14px',
  },
  '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
    paddingLeft: '14px',
    paddingRight: '9px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '13px 0',
    height: 'auto',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    paddingTop: '13px',
    paddingBottom: '13px',
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-sizeSmall': {
    transform: 'translate(12px, 13px) scale(1)',
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-sizeSmall.MuiInputLabel-shrink': {
    transform: 'translate(12px, -9px) scale(0.75)',
  },
};

const BusinessCredits = ({ organizations, refetch, mode }: any) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [order, setOrder] = useState<Order>(DEFAULT_ORDER as Order);
  const [orderBy, setOrderBy] = useState<string>(DEFAULT_ORDER_BY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [revisionSearchInput, setRevisionSearchInput] = useState('');
  const [debouncedRevisionSearch, setDebouncedRevisionSearch] = useState('');
  const [projectInputValue, setProjectInputValue] = useState('');
  const [debouncedProjectInput, setDebouncedProjectInput] = useState('');
  const [selectedProject, setSelectedProject] = useState<CreditsProjectOption | null>(null);
  const { role } = useContext(UseAuthContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedRevisionSearch(revisionSearchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [revisionSearchInput]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedProjectInput(projectInputValue.trim()), 300);
    return () => window.clearTimeout(id);
  }, [projectInputValue]);

  const selectedProjectId = selectedProject?.id ?? '';

  useEffect(() => {
    setPage(0);
  }, [debouncedRevisionSearch, selectedProjectId]);
  
  const { mutate: topUpMutation, isLoading: isTopUpLoading } = useMutation(adminTopUp, {
    onSuccess: () => {
      enqueueSnackbar(t('BUSINESS_ADMIN_TRANSACTION_CREDIT_ADD_SUCCESS'), {
        variant: 'success',
      });
      queryClient.invalidateQueries(['organizations']);
      queryClient.invalidateQueries(['transactions']);
      refetch();
      formik.resetForm();
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      console.error('Error during top up:', error);
    },
  });

  const { 
    data: transactionsData,
    refetch: refetchTransactions 
  } = useQuery(
    ['transactions', organizations?.id, page, rowsPerPage, order, orderBy, debouncedRevisionSearch, selectedProjectId],
    () => getOrganizationTransactions(organizations?.id, {
      page: page + 1,
      pageSize: rowsPerPage,
      sortOrder: order,
      sortBy: orderBy,
      searchRevision: debouncedRevisionSearch,
      projectId: selectedProjectId,
    }),
    {
      enabled: mode !== 'create' && !!organizations?.id,
      refetchOnWindowFocus: true,
      onError: (error: any) => {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      }
    }
  );

  const { data: projectListData, isLoading: isLoadingProjectOptions } = useQuery({
    queryKey: ['creditsProjectOptions', organizations?.id, debouncedProjectInput],
    queryFn: () =>
      getProjectLists({
        page: 1,
        pageSize: 100,
        sortOrder: 'asc',
        sortBy: 'name',
        search: debouncedProjectInput || undefined,
        searchType: 'projectName',
      }),
    enabled: mode !== 'create' && !!organizations?.id,
    staleTime: 60 * 1000,
  });

  const projectFilterOptions: CreditsProjectOption[] = useMemo(() => {
    const rows = projectListData?.data ?? [];
    return rows.map((p: { id: string; name?: string }) => ({
      id: String(p.id),
      name: p.name ?? '',
    }));
  }, [projectListData]);

  // Fetch pricing plans from AWS to generate package options dynamically
  const { data: pricingPlansData, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['pricingPlans'],
    queryFn: getPricingPlans,
    enabled: role === USER_ROLES.ADMIN && mode !== 'create',
  });

  // Generate package_options dynamically from AWS plans
  const package_options = useMemo(() => {
    const options: Array<{ label: string; value: string; name?: string }> = [
      { label: 'PACKAGE_CUSTOM', value: 'CUSTOM', name: t('PACKAGE_CUSTOM') }
    ];

    if (pricingPlansData?.data && Array.isArray(pricingPlansData.data)) {
      pricingPlansData.data.forEach((plan: any) => {
        if (plan.ProductId && plan.ProductId !== 'SINGLE_CREDITS') {
          options.push({
            label: `PACKAGE_${plan.ProductId}`,
            value: plan.ProductId,
            name: plan.name || plan.ProductId  // Store the actual product name
          });
        }
      });
    }

    return options;
  }, [pricingPlansData, t]);

  const totalCredits = useMemo(() => {
    if (mode === 'create') return 0;
    return calcTotalAccountPoints(organizations);
  }, [organizations?.transactions, mode]);

  // Filter transactions to exclude subscription packages (unlimited)
  const creditsTransactions = useMemo(() => {
    if (!transactionsData?.data) return [];
    return transactionsData.data.filter((t: any) => !t.unlimited);
  }, [transactionsData?.data]);

  const formik = useFormik({
    initialValues: {
      packageType: '',
      credits: '',
      description: '',
      sum: ''
    },
    validationSchema: Yup.object({
      packageType: Yup.string().required(t('FIELD_REQUIRED')),
      credits: Yup.number().when('packageType', {
        is: 'CUSTOM',
        then: () => Yup.number().required(t('FIELD_REQUIRED'))
      }),
      description: Yup.string().when('packageType', {
        is: 'CUSTOM',
        then: () => Yup.string().required(t('FIELD_REQUIRED'))
      }),
    }),
    onSubmit: async (values) => {
      if (mode === 'create') {
        enqueueSnackbar(t('BUSINESS_ADMIN_CREDIT_CREATE_FIRST'), {
          variant: 'warning',
        });
        return;
      }

      const topUpData: IAdminTopUp = {
        ProdQuantity: 1,
        OrganizationId: organizations.id,
        ProductDesc: values.description,
        TransactionId: shortid.generate(),
        ProductID: values.packageType,
      };

      if (values.packageType === 'CUSTOM') {
        topUpData.suminfull = values.sum;
        topUpData.TotalPoints = values.credits;
      }
      topUpMutation(topUpData);
      refetchTransactions();
    },
  });


  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const createSortHandler = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleDownloadTransactions = async () => {
    try {
      const response = await getOrganizationTransactionsCSV(organizations.id);
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${organizations.company_name}_${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box className='business_credits_header'>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            direction: 'ltr',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {mode !== 'create' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              <Autocomplete<CreditsProjectOption, false, false, false>
                size='small'
                options={projectFilterOptions}
                loading={isLoadingProjectOptions}
                value={selectedProject}
                onChange={(_, newValue) => {
                  setSelectedProject(newValue);
                  setProjectInputValue(newValue?.name ?? '');
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'clear') {
                    setSelectedProject(null);
                    setProjectInputValue('');
                    return;
                  }
                  if (reason === 'reset') {
                    return;
                  }
                  setProjectInputValue(newInputValue);
                  if (selectedProject && newInputValue !== selectedProject.name) {
                    setSelectedProject(null);
                  }
                }}
                inputValue={projectInputValue}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                filterOptions={(options) => options}
                sx={{ width: { xs: '100%', sm: '280px' }, flex: { sm: '0 0 auto' } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size='small'
                    label={t('BUSINESS_CREDITS_PROJECT_FILTER_LABEL')}
                    sx={creditsFilterFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingProjectOptions ? (
                            <CircularProgress color='inherit' size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                      dir: 'auto',
                    }}
                  />
                )}
              />
              <TextField
                size='small'
                variant='outlined'
                label={t('BUSINESS_CREDITS_REVISION_LABEL')}
                placeholder={t('BUSINESS_CREDITS_REVISION_LABEL')}
                value={revisionSearchInput}
                onChange={(e) => setRevisionSearchInput(e.target.value)}
                sx={{ ...creditsFilterFieldSx, width: { xs: '100%', sm: '220px' }, flex: { sm: '0 0 auto' } }}
                inputProps={{
                  'aria-label': t('BUSINESS_CREDITS_REVISION_LABEL'),
                  dir: 'ltr',
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: '0 1 auto', minWidth: 0 }}>
            <TypographyLocale variant='h2' fontWeight='bold'>
              <span>{totalCredits} </span>
              <span>{t('EDIT_BUSINESS_HEADER_BALANCE')}</span>
            </TypographyLocale>
          </Box>
        </Box>
        {role === USER_ROLES.ADMIN && mode !== 'create' && (
          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Box display={"flex"} alignItems={"center"} gap={1}>
                  <Autocomplete
                    clearIcon={<></>}
                    options={package_options}
                    loading={isLoadingPlans}
                    getOptionLabel={(option: any) => option.name || option.value || t(option.label)}
                    onChange={(_, value: any) => {
                      formik.setFieldValue('packageType', value?.value);
                      if (value?.value !== 'CUSTOM') {
                        formik.setFieldValue('credits', '');
                        formik.setFieldValue('description', '');
                      }
                    }}
                    size='small'
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="packageType"
                        label={t("ADMIN_ADD_BUSINESS_CREDIT_PACKAGE_SELECT")}
                        sx={{ ...textFieldStyles, width: "220px" }}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingPlans ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <TextField
                    name="credits"
                    label={t("ADMIN_ADD_BUSINESS_CREDIT_POINTS")}
                    value={formik.values.credits}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    
                    disabled={formik.values.packageType !== 'CUSTOM'}
                    sx={textFieldStyles}
                    required={formik.values.packageType === 'CUSTOM'}
                  />
                
                  <TextField
                    name="description"
                    label={t("ADMIN_ADD_BUSINESS_CREDIT_DESCRIPTION")}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                 
                    disabled={formik.values.packageType !== 'CUSTOM'}
                    sx={textFieldStyles}
                    required={formik.values.packageType === 'CUSTOM'}
                  />
               
                  <ButtonCustom
                    color="primary"
                    type="submit"
                    variant="contained"
                    disabled={isTopUpLoading}
                    startIcon={isTopUpLoading ? <CircularProgress size={20} color='inherit'/> : null}
                  >
                    {t("SAVE_BUTTON")}
                  </ButtonCustom>
              </Box>
              <Box textAlign={"right"} alignSelf={"flex-end"}>
                {role === USER_ROLES.ADMIN && (
                  <ButtonIcon
                    onClick={handleDownloadTransactions}
                  >
                    <FileDownloadOutlinedIcon fontSize='large' />
                  </ButtonIcon>
                )}
              </Box>
            </Box>
          </form>)}

        {mode !== 'create' && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <CustomTableFontCell sx={{textAlign: 'center'}}>
                    <StyledTableSortLabel
                      active={orderBy === "type"}
                      direction={orderBy === "type" ? order : "asc"}
                      onClick={() => createSortHandler("type")}
                    >
                      {t('BUSINESS_SETTING_TRANSACTION_STATUS')}
                      {orderBy === "type" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>

                  <CustomTableFontCell>
                    <StyledTableSortLabel
                      active={orderBy === "createdAt"}
                      direction={orderBy === "createdAt" ? order : "asc"}
                      onClick={() => createSortHandler("createdAt")}
                    >
                      {t('BUSINESS_SETTING_TRANSACTION_DATE')}
                      {orderBy === "createdAt" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell>
                    <StyledTableSortLabel
                      active={orderBy === "user"}
                      direction={orderBy === "user" ? order : "asc"}
                      onClick={() => createSortHandler("user")}
                    >
                      {t('BUSINESS_SETTING_TRANSACTION_BY')}
                      {orderBy === "user" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell>
                    <StyledTableSortLabel
                      active={orderBy === "project"}
                      direction={orderBy === "project" ? order : "asc"}
                      onClick={() => createSortHandler("project")}
                    >
                      {t('EDIT_BUSINESS_TABLE_FOR')}
                      {orderBy === "project" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell>
                    <StyledTableSortLabel
                      active={orderBy === "revision_id"}
                      direction={orderBy === "revision_id" ? order : "asc"}
                      onClick={() => createSortHandler("revision_id")}
                    >
                      {t('BUSINESS_SETTING_TRANSACTION_REVISION_ID')}
                      {orderBy === "revision_id" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell>
                    <StyledTableSortLabel
                      active={orderBy === "points"}
                      direction={orderBy === "points" ? order : "asc"}
                      onClick={() => createSortHandler("points")}
                    >
                      {t('BUSINESS_SETTING_TRANSACTION_CREDITS')}
                      {orderBy === "points" ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditsTransactions.length > 0 &&
                  creditsTransactions.map((transaction) => {
                      return <Transaction
                        key={transaction.id}
                        transaction={transaction}
                      />
                    })}
              </TableBody>
            </Table>
            <TablePagination
              sx={{ width: "100%" }}
              rowsPerPageOptions={[20]}
              component='div'
              count={transactionsData?.paging?.total ?? 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('MUI_ROWS_PER_PAGE')}
              labelDisplayedRows={({ from, to, count }) => t('MUI_PAGINATION_RANGE', { from, to, count })}
              getItemAriaLabel={(type) => t(`MUI_PAGINATION_${type.toUpperCase()}_PAGE`)}
            />
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default BusinessCredits;
